import React, { useState, useEffect, useMemo } from 'react';
import api from '../../services/api';

const ProjetsModule = ({ deptId = null }) => {
  const [activeTab, setActiveTab] = useState('tableau');
  const [expandedProjet, setExpandedProjet] = useState(null);
  const [projets, setProjets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Charger les projets depuis le backend
  useEffect(() => {
    loadProjets();
  }, [deptId]);

  const loadProjets = async () => {
    try {
      setLoading(true);
      setError(null);
      const filters = deptId ? { departement_id: deptId } : {};
      const response = await api.projects.getAll(filters);
      setProjets(response.projects || []);
    } catch (err) {
      console.error('Erreur chargement projets:', err);
      setError('Erreur lors du chargement des projets');
    } finally {
      setLoading(false);
    }
  };

  // Mapper les champs de la BD aux champs attendus par l'interface
  const projetsFormatted = useMemo(() => {
    const STATUS_COLORS = {
      'en_cours': '#42a5f5',
      'planification': '#C9A84C',
      'production': '#ff9800',
      'termine': '#66bb6a',
      'annule': '#ef5350',
      'negociation': '#ab47bc',
      'retard': '#ff9800'
    };

    return projets.map(p => {
      // Extraire les initiales de l'équipe
      const equipeInitiales = (p.team || []).map(member => {
        const prenom = member.prenom || member.user?.name?.split(' ')[0] || '';
        const nom = member.nom || member.user?.name?.split(' ')[1] || '';
        return `${prenom[0] || ''}${nom[0] || ''}`;
      }).filter(x => x).slice(0, 3);

      return {
        ...p,
        nom: p.name,
        chef: p.owner?.name || 'Non assigné',
        filiale: p.filiale?.nom || 'Non assigné',
        avancement: p.progress || 0,
        depenses: parseFloat(p.depenses_reelles) || 0,
        deadline: p.end_date ? new Date(p.end_date).toLocaleDateString('fr-FR') : 'N/A',
        statut: p.status,
        color: STATUS_COLORS[p.status] || '#667',
        priorite: p.priority || 'normale',
        equipe: equipeInitiales,
      };
    });
  }, [projets]);

  const fmtN = n => Math.round(n).toLocaleString('fr-FR');
  const fmtK = n => n >= 1000000 ? (n / 1000000).toFixed(1) + 'M' : n >= 1000 ? (n / 1000).toFixed(0) + 'k' : n;

  const totalBudget = projetsFormatted.reduce((s, p) => s + (parseFloat(p.budget) || 0), 0);
  const totalDepenses = projetsFormatted.reduce((s, p) => s + p.depenses, 0);
  const avgAvancement = projetsFormatted.length > 0 ? Math.round(projetsFormatted.reduce((s, p) => s + p.avancement, 0) / projetsFormatted.length) : 0;
  const projetsActifs = projetsFormatted.filter(p => p.statut !== 'termine').length;

  return (
    <div style={{ background: '#0d1b2a', minHeight: '100vh', fontFamily: 'Outfit, sans-serif', fontSize: 14, color: '#fff' }}>
      {/* HEADER */}
      <div style={{ padding: '24px', marginBottom: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 28, fontWeight: 600, lineHeight: 1.1 }}>
              Gestion des Projets
            </div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 4 }}>
              Portefeuille complet - Suivi temps reel
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button style={{
              background: 'transparent',
              border: '1.5px solid rgba(255,255,255,0.07)',
              borderRadius: 8,
              padding: '8px 16px',
              color: '#888',
              fontSize: 12,
              cursor: 'pointer'
            }}>
              Exporter
            </button>
            <button style={{
              background: '#C9A84C',
              border: 'none',
              borderRadius: 8,
              padding: '9px 18px',
              color: '#1a1a0d',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer'
            }}>
              + Nouveau Projet
            </button>
          </div>
        </div>

        {/* KPIs */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 24 }}>
          <div style={{ background: '#162535', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 12, padding: '18px 20px', position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: '#42a5f5' }} />
            <div style={{ fontSize: 22, marginBottom: 10 }}>📁</div>
            <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 32, fontWeight: 600, lineHeight: 1, marginBottom: 4 }}>{projetsActifs}</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>Projets actifs</div>
          </div>
          <div style={{ background: '#162535', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 12, padding: '18px 20px', position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: '#C9A84C' }} />
            <div style={{ fontSize: 22, marginBottom: 10 }}>📊</div>
            <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 32, fontWeight: 600, lineHeight: 1, marginBottom: 4 }}>{avgAvancement}%</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>Avancement moyen</div>
            <div style={{ fontSize: 10, marginTop: 8, padding: '3px 8px', borderRadius: 99, display: 'inline-block', background: 'rgba(255,255,255,0.06)', color: '#888' }}>
              En cours
            </div>
          </div>
          <div style={{ background: '#162535', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 12, padding: '18px 20px', position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: '#66bb6a' }} />
            <div style={{ fontSize: 22, marginBottom: 10 }}>💰</div>
            <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 32, fontWeight: 600, lineHeight: 1, marginBottom: 4 }}>{fmtK(totalBudget)} F</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>Budget total</div>
          </div>
          <div style={{ background: '#162535', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 12, padding: '18px 20px', position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: '#ff9800' }} />
            <div style={{ fontSize: 22, marginBottom: 10 }}>💸</div>
            <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 32, fontWeight: 600, lineHeight: 1, marginBottom: 4 }}>{fmtK(totalDepenses)} F</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>Depenses cumulees</div>
            <div style={{ fontSize: 10, marginTop: 8, padding: '3px 8px', borderRadius: 99, display: 'inline-block', background: 'rgba(76,175,80,0.15)', color: '#66bb6a' }}>
              {Math.round(totalDepenses / totalBudget * 100)}% du budget
            </div>
          </div>
        </div>

        {/* TABS */}
        <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid rgba(255,255,255,0.07)', marginBottom: 20 }}>
          {[
            { id: 'tableau', label: 'Vue Tableau' },
            { id: 'gantt', label: 'Planning Gantt' },
            { id: 'kanban', label: 'Kanban' },
            { id: 'budget', label: 'Budget & Couts' }
          ].map(tab => (
            <div
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '10px 16px',
                fontSize: 12,
                color: activeTab === tab.id ? '#C9A84C' : '#667',
                cursor: 'pointer',
                borderBottom: activeTab === tab.id ? '2px solid #C9A84C' : '2px solid transparent',
                transition: 'all 0.15s'
              }}
            >
              {tab.label}
            </div>
          ))}
        </div>

        {/* TABLEAU */}
        {activeTab === 'tableau' && (
          <div>
            {loading && (
              <div style={{ padding: '40px', textAlign: 'center', color: '#C9A84C' }}>
                Chargement des projets...
              </div>
            )}
            {error && (
              <div style={{ padding: '20px', background: 'rgba(239,83,80,0.1)', border: '1px solid rgba(239,83,80,0.3)', borderRadius: 8, color: '#ef5350', marginBottom: 16 }}>
                {error}
              </div>
            )}
            {!loading && projets.length === 0 && (
              <div style={{ padding: '40px', textAlign: 'center', color: '#667' }}>
                <div style={{ fontSize: 40, marginBottom: 10 }}>📋</div>
                <div style={{ fontSize: 14 }}>Aucun projet trouvé</div>
              </div>
            )}
            {!loading && projetsFormatted.map(projet => (
              <div
                key={projet.id}
                style={{
                  background: '#162535',
                  border: '1px solid rgba(255,255,255,0.07)',
                  borderRadius: 14,
                  overflow: 'hidden',
                  marginBottom: 14,
                  transition: 'border-color 0.2s'
                }}
                onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(201,168,76,0.2)'}
                onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)'}
              >
                <div
                  onClick={() => setExpandedProjet(expandedProjet === projet.id ? null : projet.id)}
                  style={{
                    padding: '18px 20px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 16
                  }}
                >
                  <div style={{
                    width: 4,
                    borderRadius: 2,
                    flexShrink: 0,
                    alignSelf: 'stretch',
                    background: projet.color
                  }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 15, fontWeight: 600, color: '#fff', marginBottom: 4 }}>{projet.nom}</div>
                    <div style={{ fontSize: 11, color: '#667', display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 10 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <span>🏢</span> {projet.filiale}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <span>👤</span> {projet.chef}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <span>📅</span> {projet.deadline}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <span>💰</span> {fmtN(projet.budget)} F
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
                      <span style={{
                        fontSize: 10,
                        fontWeight: 600,
                        padding: '3px 9px',
                        borderRadius: 99,
                        background: projet.statut === 'termine' ? 'rgba(76,175,80,0.15)' : projet.statut === 'retard' ? 'rgba(239,83,80,0.15)' : 'rgba(66,165,245,0.15)',
                        color: projet.statut === 'termine' ? '#66bb6a' : projet.statut === 'retard' ? '#ef8080' : '#64b5f6'
                      }}>
                        {projet.statut === 'termine' ? '✅ Termine' : projet.statut === 'retard' ? '⚠️ En retard' : '🔄 En cours'}
                      </span>
                      <span style={{
                        fontSize: 10,
                        fontWeight: 600,
                        padding: '3px 9px',
                        borderRadius: 99,
                        background: projet.priorite === 'urgente' ? 'rgba(239,83,80,0.15)' : projet.priorite === 'haute' ? 'rgba(255,152,0,0.15)' : 'rgba(66,165,245,0.15)',
                        color: projet.priorite === 'urgente' ? '#ef8080' : projet.priorite === 'haute' ? '#ffb74d' : '#64b5f6'
                      }}>
                        {projet.priorite === 'urgente' ? '🔴 Urgente' : projet.priorite === 'haute' ? '🟠 Haute' : '🔵 Normale'}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 11, color: '#aaa', marginBottom: 4 }}>Avancement: {projet.avancement}%</div>
                        <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: 99, height: 5, overflow: 'hidden' }}>
                          <div style={{
                            width: `${projet.avancement}%`,
                            height: '100%',
                            borderRadius: 99,
                            background: projet.avancement >= 80 ? '#66bb6a' : projet.avancement >= 50 ? '#ffb74d' : '#ef8080',
                            transition: 'width 0.8s ease'
                          }} />
                        </div>
                      </div>
                      <div style={{ fontSize: 11, color: '#667' }}>
                        Depense: {Math.round(projet.depenses / projet.budget * 100)}%
                      </div>
                    </div>
                  </div>
                  <div style={{
                    fontSize: 16,
                    color: '#C9A84C',
                    transition: 'transform 0.25s',
                    flexShrink: 0,
                    marginTop: 2,
                    transform: expandedProjet === projet.id ? 'rotate(180deg)' : 'rotate(0deg)'
                  }}>
                    ▼
                  </div>
                </div>
                
                {expandedProjet === projet.id && (
                  <div style={{ padding: '0 20px 20px', borderTop: '1px solid rgba(255,255,255,0.07)' }}>
                    <div style={{ paddingTop: 16, fontSize: 12, color: '#aaa', marginBottom: 12 }}>
                      {projet.description}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 600, color: '#C9A84C', marginBottom: 8 }}>EQUIPE</div>
                        <div style={{ display: 'flex', gap: 6 }}>
                          {projet.equipe.map((membre, i) => (
                            <div key={i} style={{
                              width: 28,
                              height: 28,
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: 10,
                              fontWeight: 700,
                              background: `${projet.color}33`,
                              color: projet.color,
                              border: '2px solid #162535'
                            }}>
                              {membre}
                            </div>
                          ))}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 600, color: '#C9A84C', marginBottom: 8 }}>BUDGET</div>
                        <div style={{ fontSize: 11, color: '#aaa' }}>
                          {fmtN(projet.depenses)} / {fmtN(projet.budget)} F
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Other tabs - Placeholder */}
        {activeTab !== 'tableau' && (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>
              {activeTab === 'gantt' && '📊'}
              {activeTab === 'kanban' && '📋'}
              {activeTab === 'budget' && '💰'}
            </div>
            <div style={{ fontSize: 16, color: '#C9A84C', marginBottom: 8 }}>
              {activeTab === 'gantt' && 'Planning Gantt'}
              {activeTab === 'kanban' && 'Vue Kanban'}
              {activeTab === 'budget' && 'Budget & Couts'}
            </div>
            <div style={{ fontSize: 13, color: '#667' }}>
              Vue a venir
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjetsModule;
